import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import Sidebar from '../../components/Sidebar';
import '../../styles/ClassesListPage.css';
import '../../styles/ClassesListPage.fix.css';
import '../../styles/ModalStyles.css';
import '../../styles/DashboardLayout.css';

const ClassesListPage = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ search: '', teacher: '', status: '' });
  const [teachers, setTeachers] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch classes (halaqas)
        const classesResponse = await api.get('/halaqas', {
          params: {
            search: filters.search || undefined,
            teacher: filters.teacher || undefined,
            status: filters.status || undefined
          }
        });

        const classesData = classesResponse.data.success
          ? classesResponse.data.data
          : classesResponse.data;

        const classesArray = Array.isArray(classesData) ? classesData : [];

        // Fetch student counts for each class
        const classesWithStudentCounts = await Promise.all(
          classesArray.map(async (classItem) => {
            try {
              // Fetch students for each class
              const studentsResponse = await api.get(`/halaqas/${classItem._id}/students`);
              const studentsData = studentsResponse.data.success
                ? studentsResponse.data.data
                : studentsResponse.data;

              // Return class with student count added
              return {
                ...classItem,
                studentCount: Array.isArray(studentsData) ? studentsData.length : 0
              };
            } catch (err) {
              console.error(`Error fetching students for class ${classItem._id}:`, err);
              // Return class with student count of 0 in case of error
              return {
                ...classItem,
                studentCount: 0
              };
            }
          })
        );

        setClasses(classesWithStudentCounts);

        // Fetch teachers for the filter
        const teachersResponse = await api.get('/users', {
          params: { role: 'teacher' }
        });

        const teachersData = teachersResponse.data.success
          ? teachersResponse.data.data
          : teachersResponse.data;

        setTeachers(Array.isArray(teachersData) ? teachersData : []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load classes. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };


  return (
    <div className="dashboard-container">
      {/* Mobile Header - without hamburger menu */}
      <div className="mobile-header">
        <h1 className="app-title">Classes Management</h1>
        <div className="avatar">👤</div>
      </div>

      {/* Dashboard Layout */}
      <div className="dashboard-layout">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        
        {/* Main Content */}
        <div className="dashboard-content">
          <div className="classes-list-container">
            <div className="header">
              <div className="header-left">
                <Link to="/dashboard" className="back-to-dashboard">
                  &larr; Back to Dashboard
                </Link>
                <h1>Classes Management</h1>
              </div>
              <Link to="/classes/new" className="create-class-btn">
                Create New Class
              </Link>
            </div>

      <div className="filters-container">
        <div className="filter">
          <label htmlFor="search">Search:</label>
          <input 
            type="text" 
            id="search" 
            name="search" 
            value={filters.search} 
            onChange={handleFilterChange}
            placeholder="Search by name or description..."
          />
        </div>

        <div className="filter">
          <label htmlFor="teacher">Teacher:</label>
          <select 
            id="teacher" 
            name="teacher" 
            value={filters.teacher} 
            onChange={handleFilterChange}
          >
            <option value="">All Teachers</option>
            {teachers.map(teacher => (
              <option key={teacher._id} value={teacher._id}>
                {teacher.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter">
          <label htmlFor="status">Status:</label>
          <select 
            id="status" 
            name="status" 
            value={filters.status} 
            onChange={handleFilterChange}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading classes...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          <div className="classes-count">
            Showing {classes.length} class{classes.length !== 1 ? 'es' : ''}
          </div>
          
          <div className="classes-grid">
            {classes.length > 0 ? (
              classes.map(classItem => (
                <div 
                  key={classItem._id} 
                  className={`class-card ${classItem.active ? 'active' : 'inactive'} clickable-card`}
                  onClick={() => window.location.href = `/classes/${classItem._id}`}
                >
                  <div className="class-header">
                    <h2>{classItem.name}</h2>
                    <span className={`status-badge ${classItem.active ? 'status-active' : 'status-inactive'}`}>
                      {classItem.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="class-meta">
                    <div className="meta-item">
                      <span className="label">Students:</span>
                      <span className="value">{classItem.studentCount || 0}</span>
                    </div>

                    <div className="meta-item">
                      <span className="label">Teachers:</span>
                      <span className="value">{classItem.teachers?.length || 0}</span>
                    </div>

                    <div className="meta-item">
                      <span className="label">Schedule:</span>
                      <span className="value">{classItem.time || classItem.schedule || 'Not set'}</span>
                    </div>
                  </div>
                  
                  <div className="class-description">
                    {classItem.description || 'No description provided'}
                  </div>
                  
                </div>
              ))
            ) : (
              <div className="no-results">
                No classes found matching your criteria
              </div>
            )}
          </div>
        </>
      )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassesListPage;